import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import MarketingLayout from "@/components/MarketingLayout";
import { useAuth } from "@/context/AuthContext";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      if (next) nav(next);
      else nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <MarketingLayout>
      <section className="max-w-md mx-auto px-5 py-16">
        <div className="text-center">
          <div className="bx-chip mx-auto"><Lucide.LogIn size={12} /> WELCOME BACK</div>
          <h1 className="bx-heading mt-3 text-3xl font-extrabold">Login to BINAREX.CO</h1>
          <p className="text-slate-400 text-sm mt-2">Use your email & password to access your dashboard.</p>
        </div>
        <form onSubmit={submit} className="bx-card p-6 mt-8 space-y-4">
          <div>
            <label className="text-xs text-slate-400">Email</label>
            <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bx-input mt-1" required />
          </div>
          <div>
            <label className="text-xs text-slate-400">Password</label>
            <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bx-input mt-1" required />
          </div>
          <button data-testid="login-submit" type="submit" disabled={busy} className="bx-btn-primary w-full justify-center">
            {busy ? <Lucide.Loader2 className="animate-spin" size={16} /> : <Lucide.LogIn size={16} />} Login
          </button>
          <div className="text-center text-sm text-slate-400">
            Don't have an account? <Link to="/register" className="text-purple-300 underline">Sign up</Link>
          </div>
        </form>
      </section>
    </MarketingLayout>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState(refFromUrl);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { toast.error("Please accept the Terms"); return; }
    if (password.length < 6) { toast.error("Password must be 6+ characters"); return; }
    setBusy(true);
    try {
      await register(name, email, password, referralCode);
      toast.success("Welcome to BINAREX.CO! 200 free credits added.");
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <MarketingLayout>
      <section className="max-w-md mx-auto px-5 py-16">
        <div className="text-center">
          <div className="bx-chip mx-auto"><Lucide.UserPlus size={12} /> CREATE ACCOUNT</div>
          <h1 className="bx-heading mt-3 text-3xl font-extrabold">Join BINAREX.CO</h1>
          <p className="text-slate-400 text-sm mt-2">Get 200 free credits to try every AI tool.</p>
        </div>
        <form onSubmit={submit} className="bx-card p-6 mt-8 space-y-4">
          <div>
            <label className="text-xs text-slate-400">Full Name</label>
            <input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} className="bx-input mt-1" required />
          </div>
          <div>
            <label className="text-xs text-slate-400">Email Address</label>
            <input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bx-input mt-1" required />
          </div>
          <div>
            <label className="text-xs text-slate-400">Password</label>
            <input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bx-input mt-1" required minLength={6} />
          </div>
          <div>
            <label className="text-xs text-slate-400">Referral Code (optional)</label>
            <input data-testid="register-referral" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} className="bx-input mt-1 bx-mono uppercase" placeholder="e.g. AYUS3F7Q" />
          </div>
          <label className="flex items-start gap-2 text-xs text-slate-400">
            <input data-testid="register-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" />
            I agree to the <Link to="/terms" className="text-purple-300 underline">Terms &amp; Conditions</Link> and <Link to="/privacy" className="text-purple-300 underline">Privacy Policy</Link>.
          </label>
          <button data-testid="register-submit" type="submit" disabled={busy} className="bx-btn-primary w-full justify-center">
            {busy ? <Lucide.Loader2 className="animate-spin" size={16} /> : <Lucide.UserPlus size={16} />} Create Account
          </button>
          <div className="text-center text-sm text-slate-400">
            Already have an account? <Link to="/login" className="text-purple-300 underline">Login</Link>
          </div>
        </form>
      </section>
    </MarketingLayout>
  );
}
